import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, CheckCircle2, Star } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { COMPANY, CLIENT_TYPES, IMAGES, SERVICE_IMG } from "@/data/company";
import { Icon } from "@/lib/icons";
import { Reveal, Parallax } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";
import CallbackForm from "@/components/site/CallbackForm";
import { useSeo } from "@/lib/useSeo";

export default function HomePage() {
  useSeo("Maçonnerie générale & aménagements extérieurs à Saint-Victoret",
    "SMT Provence : maçonnerie générale, béton, murs, clôtures, rénovation et aménagements extérieurs pour particuliers, professionnels et collectivités en Provence. Devis gratuit.");
  const [services, setServices] = useState([]);
  const [realisations, setRealisations] = useState([]);
  const [partners, setPartners] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/realisations", { params: { featured: true } }).then((r) => setRealisations(r.data.slice(0, 3))).catch(() => {});
    api.get("/partners", { params: { category: "partenaire" } }).then((r) => setPartners(r.data)).catch(() => {});
    api.get("/partners", { params: { category: "client" } }).then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const LogoItem = ({ p }) => p.logo
    ? <img src={fileUrl(p.logo)} alt={p.name} className="h-10 sm:h-12 w-auto object-contain grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition" />
    : <span className="font-heading font-black text-xl sm:text-2xl text-ink/25 hover:text-ink/60 transition-colors whitespace-nowrap">{p.name}</span>;

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 150]);
  const heroScale = useTransform(scrollY, [0, 800], [1, 1.12]);
  const heroTextY = useTransform(scrollY, [0, 500], [0, 90]);
  const heroTextOpacity = useTransform(scrollY, [0, 450], [1, 0]);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center pt-24 pb-16 overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute -top-[10%] left-0 right-0 h-[120%] will-change-transform">
          <video
            src={fileUrl(COMPANY.heroVideo)}
            autoPlay muted loop playsInline preload="auto"
            poster={fileUrl(IMAGES.hero)}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/70 to-ink/30" />
        </motion.div>
        <motion.div style={{ y: heroTextY, opacity: heroTextOpacity }} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="overline text-primary">Maçonnerie · Béton · Aménagements extérieurs</span>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.02]">
              Nous bâtissons du <span className="text-primary">solide</span>, des fondations aux finitions.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/80 leading-relaxed max-w-xl">
              SMT Provence réalise vos travaux de maçonnerie générale et d'aménagements extérieurs à Saint-Victoret et dans tout le 13.
              Pour les particuliers, les professionnels et les collectivités.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="hero-cta-devis" className="inline-flex items-center gap-2 bg-primary text-white px-7 py-4 rounded-full font-bold hover:brightness-95 transition">
                Devis gratuit <ArrowRight size={18} />
              </Link>
              <a href={`tel:${COMPANY.phoneHref}`} data-testid="hero-cta-call" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/30 text-white px-7 py-4 rounded-full font-bold hover:bg-white/20 transition">
                <Phone size={18} /> {COMPANY.phone}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/70">
              {["Devis gratuit & sans engagement", "Intervention rapide", "Ouvrages garantis"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-primary" /> {t}</span>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* MÉTIERS MARQUEE */}
      <section className="bg-ink border-y border-white/10 py-4">
        <Marquee>
          {services.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`metier-${s.slug}`}
              className="mx-2.5 inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-white/15 text-white/80 hover:text-primary hover:border-primary transition whitespace-nowrap">
              <Icon name={s.icon} size={18} className="text-primary" />
              <span className="font-heading font-bold text-sm uppercase tracking-wide">{s.title}</span>
            </Link>
          ))}
        </Marquee>
      </section>

      {/* CLIENT TYPES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <Reveal><span className="overline text-primary">Ils nous font confiance</span>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-black text-ink max-w-2xl">Une expertise au service de tous vos projets</h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {CLIENT_TYPES.map((c, i) => (
            <Reveal key={c.key} delay={i * 0.1}>
              <div className="h-full bg-card border border-border rounded-2xl p-8 hover:border-primary transition-colors group" data-testid={`client-type-${c.key}`}>
                <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                  <Icon name={c.icon} size={24} />
                </div>
                <h3 className="mt-5 font-heading text-xl font-bold text-ink">{c.label}</h3>
                <p className="mt-2 text-sm text-ink/60 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-ink py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <Reveal><span className="overline text-primary">Nos domaines d'intervention</span>
              <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-black text-white max-w-xl">Six savoir-faire, une seule exigence</h2>
            </Reveal>
            <Link to="/services" className="text-primary font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">Tous les services <ArrowRight size={16} /></Link>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="block group relative overflow-hidden rounded-2xl h-64">
                  <img src={fileUrl(SERVICE_IMG[s.slug])} alt={s.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center text-white mb-3"><Icon name={s.icon} size={20} /></div>
                    <h3 className="font-heading text-xl font-bold text-white">{s.title}</h3>
                    <p className="mt-1 text-sm text-white/60 line-clamp-2">{s.short}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARTENAIRES MARQUEE */}
      {partners.length > 0 && (
        <section className="py-12 border-y border-border bg-white overflow-hidden">
          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-ink/40 mb-7">Nos partenaires</p>
          <Marquee>
            {partners.map((p, i) => (
              <div key={i} className="mx-8 flex items-center" data-testid={`partner-${i}`}><LogoItem p={p} /></div>
            ))}
          </Marquee>
        </section>
      )}

      {/* REALISATIONS */}
      {realisations.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <Reveal><span className="overline text-primary">Nos réalisations</span>
              <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-black text-ink max-w-xl">Des chantiers qui parlent pour nous</h2>
            </Reveal>
            <Link to="/realisations" className="text-primary font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">Voir toutes les réalisations <ArrowRight size={16} /></Link>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {realisations.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.1}>
                <Link to={`/realisations/${r.slug}`} data-testid={`realisation-card-${r.slug}`} className="block group">
                  <div className="overflow-hidden rounded-2xl aspect-[4/3]">
                    <img src={fileUrl(r.cover_image)} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <span className="mt-4 inline-block overline text-primary">{r.category}</span>
                  <h3 className="mt-1 font-heading text-lg font-bold text-ink group-hover:text-primary transition-colors">{r.title}</h3>
                  <p className="text-sm text-ink/50">{r.location}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* ABOUT */}
      <section className="bg-secondary/40 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="rounded-2xl w-full overflow-hidden aspect-[4/5] max-h-[520px]">
                <Parallax offset={40} className="h-full relative">
                  <img src={fileUrl(IMAGES.about)} alt="Artisan maçon SMT Provence" className="w-full h-[120%] object-cover" />
                </Parallax>
              </div>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}
                className="absolute -bottom-6 -right-4 sm:right-6 bg-primary text-white rounded-2xl p-6 shadow-xl">
                <p className="font-heading text-4xl font-black">100%</p>
                <p className="text-sm font-semibold">Chantiers livrés dans le respect des normes</p>
              </motion.div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <span className="overline text-primary">Qui sommes-nous</span>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-black text-ink">Un artisan de confiance en Provence</h2>
            <p className="mt-5 text-ink/70 leading-relaxed">
              SMT Provence met son savoir-faire au service de la maçonnerie générale et des aménagements extérieurs.
              Du terrassement aux finitions, nous réalisons chaque ouvrage avec rigueur, pour garantir solidité et pérennité.
            </p>
            <ul className="mt-6 space-y-3">
              {["Devis personnalisé, gratuit et sans engagement", "Interlocuteur unique du début à la fin", "Matériaux de qualité et travail soigné", "Zone d'intervention : tout le département 13"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-ink/80"><CheckCircle2 size={20} className="text-primary mt-0.5 shrink-0" /> {t}</li>
              ))}
            </ul>
            <Link to="/realisations" className="mt-8 inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 rounded-full font-bold hover:bg-primary transition">
              Découvrir nos chantiers <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA + FORM */}
      <section id="devis" className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0"><Parallax offset={70} className="absolute -top-[12%] left-0 right-0 h-[124%]"><img src={fileUrl(IMAGES.cta)} alt="" className="w-full h-full object-cover" /></Parallax><div className="absolute inset-0 bg-ink/90" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex gap-1 text-primary mb-3">{[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor" />)}</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black text-white">Un projet ? Parlons-en.</h2>
            <p className="mt-4 text-white/70 leading-relaxed max-w-lg">
              Laissez-nous vos coordonnées : nous vous rappelons gratuitement pour étudier votre projet et vous proposer un devis sur mesure, sans engagement.
            </p>
            <a href={`tel:${COMPANY.phoneHref}`} className="mt-6 inline-flex items-center gap-3 text-white font-heading text-2xl font-black hover:text-primary transition">
              <Phone /> {COMPANY.phone}
            </a>
          </div>
          <div className="bg-white/5 backdrop-blur-xl border border-white/15 rounded-2xl p-6 sm:p-8">
            <CallbackForm dark source="home" />
          </div>
        </div>
      </section>

      {/* CLIENTS — juste au-dessus du footer */}
      {clients.length > 0 && (
        <section className="py-12 bg-background overflow-hidden border-t border-border">
          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-ink/40 mb-7">Ils nous ont fait confiance</p>
          <Marquee reverse>
            {clients.map((p, i) => (
              <div key={i} className="mx-10 flex items-center" data-testid={`client-${i}`}><LogoItem p={p} /></div>
            ))}
          </Marquee>
        </section>
      )}
    </div>
  );
}
